import { ColumnInstance, Row, useTable } from 'react-table';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CustomHeaderColumn } from './columns/PartyHeaderColumn';
import { KTCardBody } from '../../../../../_metronic/helpers';
import { PartysColumns } from './columns/_columns';
import { PartysListLoading } from '../components/loading/CustomersListLoading';
import { PartysListPagination } from '../components/pagination/CustomersListPagination';
import { Party } from '../core/_models';
import { redirectPartyList } from 'src/store/commonlist/actions';
import { useQueryResponseData, useQueryResponseLoading } from '../core/QueryResponseProvider';
import { SET_RECEIVER_ID } from 'src/store/balanceShow/types';

interface PartiesTableProps {
  showParty: boolean;
}

const PartysTable: React.FC<PartiesTableProps> = ({ showParty }) => {
  const Parties = useQueryResponseData();
  const isLoading = useQueryResponseLoading();
  const data = useMemo(() => Parties, [Parties]);
  const navigate = useNavigate();
  const columns = useMemo(() => PartysColumns, []);
  const dispatch = useDispatch();

  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });
  useEffect(() => {
    if (selectedRowIndex !== null && rowRefs.current[selectedRowIndex]) {
      rowRefs.current[selectedRowIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedRowIndex]);
  const handleRowClick = (row: Row<Party>, index: number, event: React.MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement;
    const cell = target.closest('td');

    if (cell) {
      const cellIndex = cell.cellIndex;
      const column = headerGroups[0].headers[cellIndex];

      if (column.id === 'actions') return;

      if (showParty) {
        dispatch({ type: SET_RECEIVER_ID, payload: row.original.party_code });
        navigate('/report/party-ledger');
      } else {
        setSelectedRowIndex(index);
        if (tableContainerRef.current) {
          tableContainerRef.current.focus();
        }
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!rows.length || showParty) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedRowIndex(prev => (prev === null ? 0 : Math.min(prev + 1, rows.length - 1)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedRowIndex(prev => (prev === null ? 0 : Math.max(prev - 1, 0)));
    }
  };

  return (
    <KTCardBody className='py-4'>
      <div
        className='table-responsive'
        ref={tableContainerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        style={{ outline: 'none' }}
      >
        <table
          id='kt_table_Partys'
          className='table align-middle table-row-dashed fs-6 gy-5 table-hover dataTable no-footer'
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map(headerGroup => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                className='text-center text-gray-700 fw-bolder fs-6 text-uppercase gs-0'
              >
                {headerGroup.headers.map((column: ColumnInstance<Party>) => (
                  <CustomHeaderColumn key={column.id} column={column} />
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.length > 0 ? (
              rows.map((row: Row<Party>, i: number,) => {
                prepareRow(row);
                const isSelected = i === selectedRowIndex;

                return (
                  <tr
                    {...row.getRowProps()}
                    key={`row-${i}-${row.id}`}
                    className='text-gray-600'
                    ref={el => rowRefs.current[i] = el}
                    onClick={(e) => handleRowClick(row, i, e)}
                    style={{
                      cursor: 'pointer',
                      outline: !showParty && isSelected ? '2px solid blue' : 'none',
                      outlineOffset: '-2px',
                    }}
                  >
                    {row.cells.map((cell, index) => {
                      const cellProps = cell.getCellProps();
                      const isActionColumn = cell.column.id === 'actions';

                      return (
                        <td
                          {...cellProps}
                          key={index}
                          className={isActionColumn ? 'action-column' : 'clickable-column'}
                          style={{
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            userSelect: 'none',
                            border: 'none',
                          }}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length}>
                  <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                    No matching records found
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <PartysListPagination />
      {isLoading && <PartysListLoading />}
    </KTCardBody>
  );
};


export {PartysTable};